import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppInsightsContext,
  withAITracking,
} from '@microsoft/applicationinsights-react-js';

import { safeCallWithSentry } from '@xemplo/safe-call';

import { ai } from './app-insights.service';
import { AppInsightsProviderProps } from './app-insights.types';

import styles from './app-insights.module.css';

function TelemetryBoundary({ children }: PropsWithChildren) {
  return children;
}

function AppInsightsProvider({
  children,
  instrumentationKey,
  cloudRole,
  appName,
  customConfig,
}: AppInsightsProviderProps) {
  const initialized = useRef(false);
  const [plugin, setPlugin] = useState(instrumentationKey ? ai.reactPlugin : null);

  const TrackedBoundary = useMemo(() => {
    if (!plugin) return null;

    return withAITracking(
      plugin,
      TelemetryBoundary,
      'TelemetryBoundary',
      styles.insightsWrapper,
    );
  }, [plugin]);

  useEffect(() => {
    if (!instrumentationKey?.trim()) return;
    if (initialized.current) return;

    safeCallWithSentry(() => {
      ai.initialize({
        instrumentationKey,
        cloudRole,
        appName,
        customConfig,
      });
      setPlugin(ai.reactPlugin);
    });

    initialized.current = true;
  }, [instrumentationKey, cloudRole, appName, customConfig]);

  if (instrumentationKey && !initialized.current) return null;

  return (
    <AppInsightsContext.Provider value={ai.contextPlugin}>
      {TrackedBoundary ? <TrackedBoundary>{children}</TrackedBoundary> : children}
    </AppInsightsContext.Provider>
  );
}

export default AppInsightsProvider;
